import { Close, Inventory2, MonitorWeight, Receipt } from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  ListItem,
  ListItemButton,
  Radio,
  RadioGroup,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import dayjs from "dayjs";
import pluralize from "pluralize";
import { useCallback, useMemo, useState } from "react";
import { List, type RowComponentProps } from "react-window";
import ConfirmDialog from "../../../components/shared/ConfirmDialog";
import { LoadingButton } from "../../../components/shared/LoadingButton";
import BoxesIcon from "../../../components/shared/icons/BoxesIcon";
import PalletsIcon from "../../../components/shared/icons/PalletsIcon";
import Loading from "../../../components/shared/layout/Loading";
import { toastMessage } from "../../../constants/strings";
import { formatNumber } from "../../../constants/utils";
import { useIsFeatureFlagEnabled } from "../../../featureFlags/useIsFeatureFlagEnabled";
import { useOSAD } from "../../../hooks/react-query/operations/useOSAD";
import { useReasonCodes } from "../../../hooks/react-query/operations/useReasonCodes";
import { useGetInboundTraps } from "../../../hooks/react-query/preplanning/useTraps";
import { useAssignShipments } from "../../../hooks/react-query/useAssignShipments";
import { useAllRoutesTable } from "../../../hooks/react-query/useDeliveryRoutes";
import { useDateSearchParamOrFallbackToToday } from "../../../hooks/useDateSearchParamOrFallbackToToday";
import { useSelectedServiceCenter } from "../../../hooks/useSelectedServiceCenter";
import { useTableSelectionParam } from "../../../hooks/useTableSelectionParam";
import type {
  AssignToRequest,
  AssignToTarget,
} from "../../../services/prePlanningService.types";
import type { PlanType } from "../../../types/planning/plan.type";
import { dateToInt } from "../../../utils/dateTimeHelper";
import { handlingUnitsTransformer } from "../../../utils/handlingUnitsDashTransformer";
import { toast } from "../../../utils/snackbarHelper";
import {
  getAfterRouteStopParam,
  osadTypeTransform,
} from "../inboundPlanning/utils";
import {
  useActionsMenuContext,
  type SummaryContext,
} from "./ActionsMenuContext";
import ClosedStatusWarning from "./ClosedStatusWarning";

const radioButtonLikeSx = {
  flex: 1,
  minWidth: 0,
  margin: 0,
  justifyContent: "flex-start",
  border: "1px solid",
  borderColor: "divider",
  borderRadius: 1,
  px: 1,
  py: 0.5,
  transition: "border-color 0.2s, background-color 0.2s, color 0.2s",
  "& .MuiFormControlLabel-label": {
    lineHeight: 1,
  },
  "& .MuiRadio-root": {
    p: 0.5,
    mr: 0.5,
  },
  "&:hover": {
    borderColor: "primary.main",
    backgroundColor: "action.hover",
  },
  "& .MuiRadio-root.Mui-checked + .MuiFormControlLabel-label": {
    borderColor: "primary.main",
    color: "primary.main",
    fontWeight: 600,
  },
  "&:has(.MuiRadio-root.Mui-checked)": {
    borderColor: "primary.main",
    backgroundColor: "action.selected",
  },
  "&:has(.MuiRadio-root.Mui-focusVisible)": {
    outline: "2px solid",
    outlineColor: "primary.main",
    outlineOffset: 2,
  },
} as const;

type AssignToDialogProps = {
  onSuccess?: () => void;
  source?: PlanType;
};

export const AssignToDialog = ({ onSuccess, source }: AssignToDialogProps) => {
  // * Feature Flags
  const isCNCLTrapFeatureEnabled = useIsFeatureFlagEnabled(
    "operations-5594-CNCL-traps-treatment-client"
  );

  // * Global Hooks
  const theme = useTheme();
  const [serviceCenter] = useSelectedServiceCenter();
  const [date] = useDateSearchParamOrFallbackToToday();
  const planDate = dateToInt(date);
  const dayName = dayjs.utc(date).format("dddd");
  const isWeekend = dayName === "Saturday" || dayName === "Sunday";
  const {
    closeDialog,
    shipments,
    clearSelection,
    isUpdatingClosedRouteOrTrap,
  } = useActionsMenuContext();
  const { clearSelection: clearTableSelection } = useTableSelectionParam();

  // * States
  const determineInitialAssignValue = useCallback(
    (shipments: SummaryContext[]): PlanType => {
      const allDeliveryRoutes = shipments.every(
        (shipment) => shipment.type === "route" || shipment.type === "shipment"
      );
      const allDeliveryTraps = shipments.every(
        (shipment) => shipment.type === "trap" || shipment.type === "shipment"
      );
      return allDeliveryRoutes
        ? "delivery_route"
        : allDeliveryTraps
          ? "delivery_trap"
          : "osad";
    },
    []
  );
  const isRemoveOsadOptionEnabled = useIsFeatureFlagEnabled(
    "inbound-6343-OSAD-remove-edit-button-from-assign-dialog-client"
  );
  const isFromOsadTable =
    isRemoveOsadOptionEnabled &&
    shipments.length > 0 &&
    shipments.every((s) => s.type === "osad");

  const initialAssignValue = useMemo(
    () => determineInitialAssignValue(shipments),
    [shipments, determineInitialAssignValue]
  );
  const [selectedOption, setSelectedOption] = useState(
    source ? source : initialAssignValue
  );
  const [confirmOpen, setConfirmOpen] = useState(isWeekend);
  const [openAssignToDialog, setOpenAssignToDialog] = useState(
    isWeekend ? false : true
  );
  const [target, setTarget] = useState<AssignToTarget>({
    id: "0",
    type: "none",
    date: planDate,
    sicId: serviceCenter.id,
  });
  const [searchTerm, setSearchTerm] = useState<string>("");

  // * Queries
  const { data: routes = [], isLoading: isLoadingRoutes } = useAllRoutesTable({
    date: dateToInt(date),
    sicId: serviceCenter.id,
  });
  // Filter out routes in DEPT status (immutable targets)
  const availableRoutes = routes.filter((route) => route.status !== "DEPT");

  const { data: traps = [], isLoading: isLoadingTraps } = useGetInboundTraps({
    sicId: serviceCenter.id,
    date: dateToInt(date),
  });
  const availableTraps = isCNCLTrapFeatureEnabled
    ? traps.filter((trap) => trap.status !== "CNCL")
    : traps;

  const showClosedStatusWarning = isUpdatingClosedRouteOrTrap(target.id);

  const { data: reasons = [], isLoading: isLoadingReasons } = useReasonCodes();
  const [reasonId, setReasonId] = useState<number | null>(null);
  const [osadReasonError, setOsadReasonError] = useState<string>("");

  const normalizedSearchTerm = searchTerm.toLowerCase();
  const routesOrTraps = (
    selectedOption === "delivery_route" ? availableRoutes : availableTraps
  ).filter(
    (item) =>
      item.title.toLowerCase().includes(normalizedSearchTerm) ||
      item.name?.toLowerCase().includes(normalizedSearchTerm) ||
      String(item.bills).toLowerCase().includes(normalizedSearchTerm) ||
      String(item.pallets).toLowerCase().includes(normalizedSearchTerm) ||
      String(item.pieces).toLowerCase().includes(normalizedSearchTerm) ||
      String(item.handlingUnits).toLowerCase().includes(normalizedSearchTerm) ||
      String(item.weight).toLowerCase().includes(normalizedSearchTerm)
  );

  // * Mutations
  const assignShipments = useAssignShipments({
    onSuccess: ({ affectedShipmentsCount }) => {
      clearSelection?.();
      clearTableSelection();
      if (onSuccess) onSuccess();
      toast(toastMessage.inbound.assign.success(affectedShipmentsCount), {
        variant: "success",
      });
      closeDialog();
    },
  });
  const osadAssignment = useOSAD({
    sicId: serviceCenter.id,
    onSuccess: ({ affectedShipmentsCount }) => {
      clearSelection?.();
      clearTableSelection();
      if (onSuccess) onSuccess();
      toast(toastMessage.inbound.assign.success(affectedShipmentsCount), {
        variant: "success",
      });
      closeDialog();
    },
  });

  // * Functions
  const uniqueShipmentIds = new Set(shipments.flatMap((s) => s.shipmentIds));

  const handleCloseDialog = () => {
    closeDialog();
    assignShipments.reset();
  };

  const handleTarget = (target: AssignToTarget) => {
    setTarget(target);
  };

  const createAssignToRequest = (target: AssignToTarget): AssignToRequest => {
    const origins = shipments.map((shipment) => ({
      id: String(shipment.id) === "-1" ? null : String(shipment.id),
      shipmentIds: shipment.shipmentIds,
      type: osadTypeTransform(shipment.type),
    }));
    return {
      origins: [...origins],
      target: {
        id: String(target.id) === "-1" ? null : String(target.id) || "0",
        type: target.type || "none",
        date: planDate,
        sicId: serviceCenter.id,
        ...getAfterRouteStopParam(null),
      },
    };
  };

  const handleAssignTo = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Clear any previous OSAD error
    setOsadReasonError("");

    if (uniqueShipmentIds.size === 0) {
      toast(toastMessage.inbound.assign.noShipmentsToAssign, {
        variant: "warning",
      });
    } else {
      if (selectedOption !== "osad") {
        assignShipments.mutate(createAssignToRequest(target));
      } else {
        // Validate that a reason is selected for OSAD
        if (reasonId === null) {
          setOsadReasonError("Please select a Reason for OSAD");
          return;
        }

        osadAssignment.mutate({
          reasonId,
          moduleId: null,
          shipmentIds: shipments.flatMap((shipment) => shipment.shipmentIds),
        });
      }
    }
  };

  // * Effects

  return (
    <>
      {isWeekend && (
        <ConfirmDialog
          open={confirmOpen}
          onClose={() => {
            setConfirmOpen(false);
            closeDialog();
          }}
          title="Add to Plan"
          message={
            <>
              You are adding shipments to a plan on a <strong>{dayName}</strong>
              . Do you want to proceed?
            </>
          }
          cancelButtonText="No"
          confirmButtonLabel="Yes"
          confirmButtonLoadingLabel="Proceeding…"
          isConfirmPending={false}
          onConfirm={() => {
            setConfirmOpen(false);
            setOpenAssignToDialog(true);
          }}
        />
      )}
      <Dialog
        open={openAssignToDialog}
        onClose={closeDialog}
        slotProps={{
          paper: {
            sx: {
              height: "fit-content",
            },
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: "bold" }}>
          Assign {pluralize("shipment", uniqueShipmentIds.size, true)}
        </DialogTitle>
        {showClosedStatusWarning && <ClosedStatusWarning />}
        <Box position="absolute" top={10} right={10}>
          <IconButton onClick={handleCloseDialog} aria-label="close dialog">
            <Close />
          </IconButton>
        </Box>
        <form onSubmit={handleAssignTo}>
          <DialogContent
            sx={{
              display: "flex",
              flexDirection: "column",
              flex: 1,
              minHeight: 0,
              overflow: "hidden",
              width: "550px",
            }}
          >
            <Box>
              <Typography variant="body1">
                Choose where to assign the selected shipments
              </Typography>
              <FormControl component="fieldset" sx={{ my: 2, width: "100%" }}>
                <RadioGroup
                  row
                  name="assignTo"
                  value={selectedOption}
                  onChange={(e) => {
                    setSelectedOption(e.target.value as PlanType);
                    setOsadReasonError(""); // Clear OSAD error when switching tabs
                  }}
                  sx={{ width: "100%", gap: 1 }}
                >
                  <FormControlLabel
                    value="delivery_route"
                    control={<Radio />}
                    label="Route"
                    sx={radioButtonLikeSx}
                  />
                  <FormControlLabel
                    value="delivery_trap"
                    control={<Radio />}
                    label="Trap"
                    sx={radioButtonLikeSx}
                  />
                  {!isFromOsadTable && (
                    <FormControlLabel
                      value="osad"
                      control={<Radio />}
                      label="OSAD"
                      sx={radioButtonLikeSx}
                    />
                  )}
                </RadioGroup>
              </FormControl>
            </Box>
            {(selectedOption === "delivery_route" ||
              selectedOption === "delivery_trap") && (
              <>
                <TextField
                  id="search-routes"
                  label={
                    selectedOption === "delivery_route"
                      ? "Search Routes"
                      : "Search Traps"
                  }
                  variant="outlined"
                  sx={{ mb: 3, mt: 1 }}
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                />
                {(
                  selectedOption === "delivery_route"
                    ? !isLoadingRoutes
                    : !isLoadingTraps
                ) ? (
                  <List
                    rowHeight={62}
                    rowCount={routesOrTraps.length}
                    aria-label="Assigned Shipments List"
                    style={{
                      height: 300,
                      border: "1px solid",
                      borderColor: theme.palette.divider,
                      borderLeft: 0,
                      borderRight: 0,
                    }}
                    rowProps={{
                      target,
                      handleTarget,
                      data: routesOrTraps,
                      selectedOption,
                      planDate,
                      sicId: serviceCenter.id,
                    }}
                    overscanCount={5}
                    rowComponent={RenderRow}
                  />
                ) : (
                  <Loading />
                )}
              </>
            )}
            {selectedOption === "osad" && (
              <FormControl sx={{ pt: 1 }}>
                <FormLabel id="osad-option-label">Select a reason</FormLabel>
                <RadioGroup
                  aria-labelledby="osad-option-label"
                  value={reasonId ?? ""}
                  onChange={(event) => {
                    setReasonId(Number(event.target.value));
                    setOsadReasonError(""); // Clear error when user selects a reason
                  }}
                  sx={{
                    mt: 3,
                    gap: 3,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  {!isLoadingReasons ? (
                    reasons.map((reason) => (
                      <FormControlLabel
                        key={reason.id}
                        value={reason.id}
                        control={<Radio />}
                        label={
                          <Box>
                            <Typography fontSize={14} fontWeight={600}>
                              {reason.name}
                            </Typography>
                            {reason.description && (
                              <Typography fontSize={14} color="text.secondary">
                                {reason.description}
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                    ))
                  ) : (
                    <Loading />
                  )}
                </RadioGroup>
                {osadReasonError && (
                  <Typography color="#FF0000" fontSize={14} sx={{ mt: 2 }}>
                    {osadReasonError}
                  </Typography>
                )}
              </FormControl>
            )}
          </DialogContent>
          <DialogActions sx={{ margin: "0 20px 20px 20px" }}>
            <Button variant="text" onClick={handleCloseDialog}>
              Cancel
            </Button>
            <LoadingButton
              loadingLabel="Assigning..."
              isLoading={assignShipments.isPending || osadAssignment.isPending}
            >
              Assign
            </LoadingButton>
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
};

type AssignToRowItem = {
  id: number | string;
  name?: string | null;
  title?: string | null;
  status: string;
  bills: number;
  pallets: number;
  pieces: number;
  handlingUnits: number;
  weight: number;
};

type RowData = {
  target: AssignToTarget;
  handleTarget: (value: AssignToTarget) => void;
  data: AssignToRowItem[];
  selectedOption: "delivery_route" | "delivery_trap";
  planDate: number;
  sicId: number;
};

const RenderRow = ({
  index,
  style,
  target,
  handleTarget,
  data,
  selectedOption,
  planDate,
  sicId,
}: RowComponentProps & RowData) => {
  const theme = useTheme();
  const isHandlingUnitsEnabled = useIsFeatureFlagEnabled(
    "operations-6016-handling-units-client"
  );

  const element = data[index];

  return (
    <ListItem style={style} key={index} component="div" disablePadding>
      <ListItemButton
        sx={{
          display: "block",
          borderRadius: "0.5rem",
          transition: "all",
          backgroundColor:
            String(element.id) === target.id
              ? theme.palette.action.selected
              : theme.palette.background.paper,
          "&:hover": {
            backgroundColor:
              String(element.id) === target.id
                ? theme.palette.action.selected
                : theme.palette.action.hover,
          },
        }}
        onClick={() =>
          handleTarget({
            id: String(element.id),
            date: planDate,
            sicId,
            type: selectedOption === "delivery_route" ? "route" : "trap",
          })
        }
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          flexGrow={1}
        >
          <Typography fontSize={15} fontWeight={600}>
            {element.name ? element.name : element.title}
          </Typography>
          <Chip
            label={element.status}
            size="small"
            sx={(theme) => ({
              backgroundColor: theme.palette.success.main,
              color: theme.palette.common.white,
              fontWeight: "bold",
              fontSize: 12,
              height: 24,
            })}
          />
        </Box>
        <Box display="flex" justifyContent="space-between" flexGrow={1}>
          <Box display="flex" alignItems="center" gap={1.5} mt={0.5}>
            <Box display="flex" alignItems="center" gap={0.5}>
              <Receipt
                sx={{
                  width: 16,
                  height: 16,
                  color: "gray",
                }}
              />
              <Typography fontSize={12}>
                {`${formatNumber(element.bills)} ${pluralize("Bill", element.bills)}`}
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={0.5}>
              <PalletsIcon
                sx={{
                  width: 16,
                  height: 16,
                  color: "gray",
                }}
              />
              <Typography fontSize={12}>
                {`${formatNumber(element.pallets)} ${pluralize("Plt", element.pallets)}`}
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={0.5}>
              <BoxesIcon width={16} height={16} fillColor="gray" />
              <Typography fontSize={12}>
                {`${formatNumber(element.pieces)} ${pluralize("Pc", element.pieces)}`}
              </Typography>
            </Box>
            {isHandlingUnitsEnabled && (
              <Box display="flex" alignItems="center" gap={0.5}>
                <Inventory2
                  sx={{
                    width: 16,
                    height: 16,
                    fill: "gray",
                  }}
                />
                <Typography fontSize={12}>
                  {handlingUnitsTransformer(element.handlingUnits)} HU
                </Typography>
              </Box>
            )}
            <Box display="flex" alignItems="center" gap={0.5}>
              <MonitorWeight
                sx={{
                  width: 16,
                  height: 16,
                  color: "gray",
                }}
              />
              <Typography fontSize={12}>
                {`${formatNumber(element.weight)} ${pluralize("lb", element.weight)}`}
              </Typography>
            </Box>
          </Box>
        </Box>
      </ListItemButton>
    </ListItem>
  );
};
